import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  addDays,
  addMonths,
  addWeeks,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  getHours,
  getMinutes,
  isSameDay,
  isSameMonth,
  isToday,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from "date-fns";
// Locale concreto, no el barril `date-fns/locale`: ese es ESM puro (rompe Jest)
// y además arrastraría todos los idiomas al bundle.
import { es } from "date-fns/locale/es";
import { ChevronLeft, ChevronRight, Clock, Pencil, Plus, Trash2, X } from "lucide-react";

import { UserContext } from "../../../context/UserContext";
import "./CalendarPage.css";

/**
 * Calendario (rediseño de Figma — sección Calendario, nodo 3880:63960).
 *
 * Misma API y mismo modelo que antes (`/v1/venues/{id}/events`, `/v1/events`):
 * solo cambia la presentación. Lo que aporta el rediseño:
 *  - Mes: rejilla de 7 columnas con la agenda del día seleccionado al lado,
 *    en vez de una lista debajo del calendario.
 *  - Semana/Día: rejilla horaria donde el evento ocupa su duración real
 *    (HOUR_HEIGHT px = 1 hora) en lugar de caer en la celda de su hora de inicio.
 *  - Alta/edición: el tipo se elige con chips y el color con muestras, no con
 *    dos desplegables de texto.
 */

const VIEW_OPTIONS = [
  { key: "day", label: "Día" },
  { key: "week", label: "Semana" },
  { key: "month", label: "Mes" },
];

const EVENT_TYPE_OPTIONS = [
  { value: "general", label: "General" },
  { value: "meeting", label: "Reunión" },
  { value: "social", label: "Evento social" },
  { value: "workshop", label: "Taller" },
  { value: "shift", label: "Turno" },
];

const COLOR_OPTIONS = [
  { value: "emerald", label: "Verde" },
  { value: "sky", label: "Azul" },
  { value: "amber", label: "Ámbar" },
  { value: "rose", label: "Rosa" },
  { value: "violet", label: "Violeta" },
];

/** La rejilla horaria cubre de 07:00 a 23:00; 40 px = 1 hora (igual que en Figma). */
const DAY_START_HOUR = 7;
const DAY_END_HOUR = 23;
const HOUR_HEIGHT = 40;
const HOURS = Array.from({ length: DAY_END_HOUR - DAY_START_HOUR }, (_, index) =>
  `${String(index + DAY_START_HOUR).padStart(2, "0")}:00`
);

/** Cuántos eventos caben en una celda de mes antes de resumir con "+N más". */
const MONTH_CHIP_LIMIT = 2;

const createDraft = (date = new Date(), event = null) => {
  if (event) {
    return {
      id: event.id,
      title: event.title || "",
      date: format(parseISO(event.starts_at), "yyyy-MM-dd"),
      startTime: format(parseISO(event.starts_at), "HH:mm"),
      endTime: format(parseISO(event.ends_at), "HH:mm"),
      notes: event.notes || "",
      eventType: event.event_type || "general",
      colorToken: event.color_token || "emerald",
    };
  }

  return {
    id: null,
    title: "",
    date: format(date, "yyyy-MM-dd"),
    startTime: "18:00",
    endTime: "20:00",
    notes: "",
    eventType: "general",
    colorToken: "emerald",
  };
};

function colorClassFor(colorToken) {
  const known = COLOR_OPTIONS.some((option) => option.value === colorToken);
  return `cal-ev--${known ? colorToken : "emerald"}`;
}

function typeLabelFor(eventType) {
  return EVENT_TYPE_OPTIONS.find((option) => option.value === eventType)?.label || "General";
}

function sortEvents(events) {
  return [...events].sort((left, right) => left.starts_at.localeCompare(right.starts_at));
}

/** Clave estable por día civil local (evita desajustes con toISOString() en UTC). */
function calendarDayKey(day) {
  return format(day, "yyyy-MM-dd");
}

function chunkCalendarWeeks(days) {
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }
  return weeks;
}

function formatToolbarLabel(view, date) {
  if (view === "day") {
    return format(date, "EEEE d 'de' MMMM yyyy", { locale: es });
  }
  if (view === "week") {
    const weekStart = startOfWeek(date, { locale: es });
    const weekEnd = endOfWeek(date, { locale: es });
    return `${format(weekStart, "d MMM", { locale: es })} – ${format(weekEnd, "d MMM yyyy", { locale: es })}`;
  }
  return format(date, "MMMM yyyy", { locale: es });
}

function eventDate(event) {
  return parseISO(event.starts_at);
}

function eventRange(event) {
  return `${format(parseISO(event.starts_at), "HH:mm")} – ${format(parseISO(event.ends_at), "HH:mm")}`;
}

function eventsOfDay(events, day) {
  return sortEvents(events.filter((event) => isSameDay(eventDate(event), day)));
}

/**
 * Posición y altura del bloque dentro de la rejilla horaria. Recorta a la
 * franja visible y trata como "hasta medianoche" los turnos que la cruzan
 * (ends_at <= starts_at en horas del reloj).
 */
function blockGeometry(event) {
  const start = parseISO(event.starts_at);
  const end = parseISO(event.ends_at);
  const startMinutes = getHours(start) * 60 + getMinutes(start);
  let endMinutes = getHours(end) * 60 + getMinutes(end);
  if (endMinutes <= startMinutes) {
    endMinutes = 24 * 60;
  }

  const from = Math.max(startMinutes, DAY_START_HOUR * 60);
  const to = Math.min(endMinutes, DAY_END_HOUR * 60);
  if (to <= from) {
    return null;
  }

  return {
    top: ((from - DAY_START_HOUR * 60) / 60) * HOUR_HEIGHT,
    height: Math.max(((to - from) / 60) * HOUR_HEIGHT, 20),
  };
}

function buildEventPayload(draft) {
  return {
    title: draft.title.trim(),
    event_type: draft.eventType,
    color_token: draft.colorToken,
    starts_at: new Date(`${draft.date}T${draft.startTime}:00`).toISOString(),
    ends_at: new Date(`${draft.date}T${draft.endTime}:00`).toISOString(),
    notes: draft.notes.trim() || null,
    all_day: false,
  };
}

function EventCard({ event, onEdit, onDelete }) {
  return (
    <article className={`cal-card ${colorClassFor(event.color_token)}`}>
      <span className="cal-card-bar" aria-hidden="true" />
      <div className="cal-card-body">
        <div className="cal-card-titlerow">
          <h4 className="cal-card-title" title={event.title}>{event.title}</h4>
          <span className="cal-tag">{typeLabelFor(event.event_type)}</span>
        </div>
        <p className="cal-card-time">
          <Clock size={14} aria-hidden="true" />
          <span>{eventRange(event)}</span>
        </p>
        {event.notes ? <p className="cal-card-notes">{event.notes}</p> : null}
      </div>
      <div className="cal-card-actions">
        <button type="button" className="cal-icon-btn" aria-label={`Editar ${event.title}`} onClick={() => onEdit(event)}>
          <Pencil size={16} />
        </button>
        <button
          type="button"
          className="cal-icon-btn cal-icon-btn--danger"
          aria-label={`Eliminar ${event.title}`}
          onClick={() => onDelete(event.id)}
        >
          <Trash2 size={16} />
        </button>
      </div>
    </article>
  );
}

function MonthView({ anchorDate, events, selectedDate, onSelectDate }) {
  const calendarDays = useMemo(() => {
    const rangeStart = startOfWeek(startOfMonth(anchorDate), { locale: es });
    const rangeEnd = endOfWeek(endOfMonth(anchorDate), { locale: es });
    return eachDayOfInterval({ start: rangeStart, end: rangeEnd });
  }, [anchorDate]);

  const weekRows = useMemo(() => chunkCalendarWeeks(calendarDays), [calendarDays]);

  return (
    <div className="cal-surface cal-month">
      <div className="cal-weekdays">
        {calendarDays.slice(0, 7).map((day) => (
          <span key={`wd-${calendarDayKey(day)}`} className="cal-weekday">
            {format(day, "EEE", { locale: es })}
          </span>
        ))}
      </div>
      <div className="cal-weeks">
        {weekRows.map((week) =>
          week.map((day) => {
            const dayEvents = eventsOfDay(events, day);
            const visible = dayEvents.slice(0, MONTH_CHIP_LIMIT);
            const rest = dayEvents.length - visible.length;
            const classes = [
              "cal-day",
              isSameMonth(day, anchorDate) ? "" : "is-outside",
              isToday(day) ? "is-today" : "",
              isSameDay(day, selectedDate) ? "is-selected" : "",
            ].filter(Boolean).join(" ");

            return (
              <button
                key={calendarDayKey(day)}
                type="button"
                className={classes}
                aria-pressed={isSameDay(day, selectedDate)}
                aria-label={`${format(day, "d 'de' MMMM", { locale: es })}, ${dayEvents.length} evento${dayEvents.length === 1 ? "" : "s"}`}
                onClick={() => onSelectDate(day)}
              >
                <span className="cal-day-head">
                  <span className="cal-day-number">{format(day, "d")}</span>
                </span>
                <span className="cal-day-events">
                  {visible.map((event) => (
                    <span
                      key={event.id}
                      className={`cal-chip ${colorClassFor(event.color_token)}`}
                      title={`${eventRange(event)} · ${event.title}`}
                    >
                      {event.title}
                    </span>
                  ))}
                  {rest > 0 ? <span className="cal-day-more">+{rest} más</span> : null}
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

/** Rejilla horaria compartida por las vistas de semana (7 columnas) y día (1). */
function TimeGridView({ days, events, selectedDate, onSelectDate, onOpenEvent }) {
  const single = days.length === 1;

  return (
    <div className="cal-surface cal-grid">
      <div className="cal-grid-inner" style={{ "--cal-cols": days.length, minWidth: single ? 0 : undefined }}>
        <div className="cal-grid-head">
          <span className="cal-grid-head-spacer" />
          {days.map((day) => (
            <button
              key={calendarDayKey(day)}
              type="button"
              className={[
                "cal-daycol-head",
                single ? "is-single" : "",
                isToday(day) ? "is-today" : "",
                isSameDay(day, selectedDate) ? "is-selected" : "",
              ].filter(Boolean).join(" ")}
              onClick={() => onSelectDate(day)}
            >
              <span className="cal-daycol-name">{format(day, "EEE", { locale: es })}</span>
              <span className="cal-daycol-number">{format(day, "d")}</span>
            </button>
          ))}
        </div>

        <div className="cal-grid-body">
          <div className="cal-hours">
            {HOURS.map((hour) => (
              <span key={hour} className="cal-hour-label">{hour}</span>
            ))}
          </div>

          {days.map((day) => (
            <div key={calendarDayKey(day)} className="cal-daycol">
              {HOURS.map((hour) => (
                <div key={`${calendarDayKey(day)}-${hour}`} className="cal-hour-cell" />
              ))}
              {eventsOfDay(events, day).map((event) => {
                const geometry = blockGeometry(event);
                if (!geometry) return null;
                return (
                  <button
                    key={event.id}
                    type="button"
                    className={`cal-block ${colorClassFor(event.color_token)}`}
                    style={{ top: `${geometry.top}px`, height: `${geometry.height}px` }}
                    onClick={() => onOpenEvent(event)}
                    title={`${eventRange(event)} · ${event.title}`}
                  >
                    <span className="cal-block-title">{event.title}</span>
                    <span className="cal-block-time">{eventRange(event)}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function CalendarPage() {
  const [token, , , venueID] = useContext(UserContext);
  const [view, setView] = useState("month");
  const [anchorDate, setAnchorDate] = useState(startOfDay(new Date()));
  const [selectedDate, setSelectedDate] = useState(startOfDay(new Date()));
  const [events, setEvents] = useState([]);
  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const [draftEvent, setDraftEvent] = useState(createDraft(new Date()));
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const loadEvents = async () => {
      if (!venueID || !token) {
        setEvents([]);
        return;
      }

      setIsLoading(true);
      setErrorMessage("");
      try {
        const response = await fetch(`/v1/venues/${venueID}/events`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          throw new Error("No se pudieron cargar los eventos");
        }
        const data = await response.json();
        setEvents(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("[CalendarPage] Error loading events:", error);
        setErrorMessage("No se pudieron cargar los eventos");
      } finally {
        setIsLoading(false);
      }
    };

    loadEvents();
  }, [token, venueID]);

  const weekDays = useMemo(
    () => eachDayOfInterval({
      start: startOfWeek(anchorDate, { locale: es }),
      end: endOfWeek(anchorDate, { locale: es }),
    }),
    [anchorDate]
  );

  const agendaEvents = useMemo(() => eventsOfDay(events, selectedDate), [events, selectedDate]);

  const openComposer = (date = selectedDate, event = null) => {
    setDraftEvent(createDraft(date, event));
    setErrorMessage("");
    setIsComposerOpen(true);
  };

  const saveEvent = async () => {
    if (!draftEvent.title.trim() || !venueID || !token) {
      return;
    }

    setIsSaving(true);
    setErrorMessage("");
    try {
      const payload = {
        ...buildEventPayload(draftEvent),
        venue_id: venueID,
      };

      const isEditing = Boolean(draftEvent.id);
      const response = await fetch(isEditing ? `/v1/events/${draftEvent.id}` : "/v1/events", {
        method: isEditing ? "PATCH" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(isEditing ? buildEventPayload(draftEvent) : payload),
      });

      if (!response.ok) {
        throw new Error("No se pudo guardar el evento");
      }

      const savedEvent = await response.json();
      setEvents((currentEvents) => {
        if (isEditing) {
          return currentEvents.map((event) => (event.id === savedEvent.id ? savedEvent : event));
        }
        return [...currentEvents, savedEvent];
      });
      const nextDate = parseISO(savedEvent.starts_at);
      setSelectedDate(nextDate);
      setAnchorDate(nextDate);
      setIsComposerOpen(false);
    } catch (error) {
      console.error("[CalendarPage] Error saving event:", error);
      setErrorMessage("No se pudo guardar el evento");
    } finally {
      setIsSaving(false);
    }
  };

  const deleteEvent = async (eventID) => {
    if (!token) return;
    try {
      const response = await fetch(`/v1/events/${eventID}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error("No se pudo eliminar el evento");
      }
      setEvents((currentEvents) => currentEvents.filter((event) => event.id !== eventID));
      setIsComposerOpen(false);
    } catch (error) {
      console.error("[CalendarPage] Error deleting event:", error);
      setErrorMessage("No se pudo eliminar el evento");
    }
  };

  const moveBy = (direction) => {
    const step = direction === "back" ? -1 : 1;
    const next =
      view === "day"
        ? (step < 0 ? subDays(anchorDate, 1) : addDays(anchorDate, 1))
        : view === "week"
        ? (step < 0 ? subWeeks(anchorDate, 1) : addWeeks(anchorDate, 1))
        : (step < 0 ? subMonths(anchorDate, 1) : addMonths(anchorDate, 1));
    setAnchorDate(next);
    setSelectedDate(next);
  };

  const resetToToday = () => {
    const today = startOfDay(new Date());
    setAnchorDate(today);
    setSelectedDate(today);
  };

  const selectDay = (day) => {
    setSelectedDate(day);
    setAnchorDate(day);
  };

  const isEditing = Boolean(draftEvent.id);

  return (
    <div className="cal-page">
      <header className="cal-header">
        <div className="cal-header-text">
          <h1 className="cal-title">Calendario</h1>
          <p className="cal-subtitle">Eventos, reuniones y turnos del club.</p>
        </div>
        <button type="button" className="cal-btn cal-btn--primary" onClick={() => openComposer(selectedDate)}>
          <Plus size={16} aria-hidden="true" />
          Añadir evento
        </button>
      </header>

      <div className="cal-toolbar">
        <div className="cal-nav">
          <button type="button" className="cal-btn" onClick={resetToToday}>Hoy</button>
          <button type="button" className="cal-btn cal-btn--icon" aria-label="Anterior" onClick={() => moveBy("back")}>
            <ChevronLeft size={16} />
          </button>
          <button type="button" className="cal-btn cal-btn--icon" aria-label="Siguiente" onClick={() => moveBy("forward")}>
            <ChevronRight size={16} />
          </button>
          <h2 className="cal-period">{formatToolbarLabel(view, anchorDate)}</h2>
        </div>

        <span className="cal-toolbar-spacer" />

        <div className="cal-views" role="tablist" aria-label="Vista del calendario">
          {VIEW_OPTIONS.map((option) => (
            <button
              key={option.key}
              type="button"
              role="tab"
              aria-selected={view === option.key}
              className={`cal-view-tab${view === option.key ? " is-active" : ""}`}
              onClick={() => setView(option.key)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {errorMessage ? <div className="cal-error" role="alert">{errorMessage}</div> : null}

      {isLoading ? (
        <div className="cal-state">Cargando eventos…</div>
      ) : (
        <div className="cal-body">
          {view === "month" ? (
            <>
              <MonthView
                anchorDate={anchorDate}
                events={events}
                selectedDate={selectedDate}
                onSelectDate={selectDay}
              />
              <aside className="cal-surface cal-agenda" aria-label="Agenda del día seleccionado">
                <div>
                  <p className="cal-agenda-overline">AGENDA</p>
                  <div className="cal-agenda-daterow">
                    <h3 className="cal-agenda-date">
                      {format(selectedDate, "EEEE d 'de' MMMM", { locale: es })}
                    </h3>
                    <span className="cal-pill">
                      {agendaEvents.length} evento{agendaEvents.length === 1 ? "" : "s"}
                    </span>
                  </div>
                </div>
                <div className="cal-agenda-list">
                  {agendaEvents.length === 0 ? (
                    <p className="cal-agenda-empty">No hay eventos este día.</p>
                  ) : (
                    agendaEvents.map((event) => (
                      <EventCard
                        key={event.id}
                        event={event}
                        onEdit={(target) => openComposer(eventDate(target), target)}
                        onDelete={deleteEvent}
                      />
                    ))
                  )}
                </div>
              </aside>
            </>
          ) : (
            <TimeGridView
              days={view === "week" ? weekDays : [anchorDate]}
              events={events}
              selectedDate={selectedDate}
              onSelectDate={selectDay}
              onOpenEvent={(event) => openComposer(eventDate(event), event)}
            />
          )}
        </div>
      )}

      {isComposerOpen ? (
        <div className="cal-overlay" role="dialog" aria-modal="true" aria-label={isEditing ? "Editar evento" : "Nuevo evento"}>
          <div className="cal-dialog">
            <div className="cal-dialog-head">
              <h2 className="cal-dialog-title">{isEditing ? "Editar evento" : "Nuevo evento"}</h2>
              <button type="button" className="cal-icon-btn" aria-label="Cerrar" onClick={() => setIsComposerOpen(false)}>
                <X size={16} />
              </button>
            </div>

            <label className="cal-field">
              <span className="cal-label">Título</span>
              <input
                type="text"
                className="cal-input"
                value={draftEvent.title}
                onChange={(event) => setDraftEvent((draft) => ({ ...draft, title: event.target.value }))}
                placeholder="Cata de variedades, junta directiva…"
              />
            </label>

            <div className="cal-field">
              <span className="cal-label" id="cal-tipo-label">Tipo</span>
              <div className="cal-options" role="group" aria-labelledby="cal-tipo-label">
                {EVENT_TYPE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={draftEvent.eventType === option.value}
                    className={`cal-option${draftEvent.eventType === option.value ? " is-selected" : ""}`}
                    onClick={() => setDraftEvent((draft) => ({ ...draft, eventType: option.value }))}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="cal-field">
              <span className="cal-label" id="cal-color-label">Color</span>
              <div className="cal-swatches" role="group" aria-labelledby="cal-color-label">
                {COLOR_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    aria-label={option.label}
                    aria-pressed={draftEvent.colorToken === option.value}
                    className={`cal-swatch ${colorClassFor(option.value)}${draftEvent.colorToken === option.value ? " is-selected" : ""}`}
                    onClick={() => setDraftEvent((draft) => ({ ...draft, colorToken: option.value }))}
                  >
                    <span className="cal-swatch-dot" />
                  </button>
                ))}
              </div>
            </div>

            <div className="cal-datetime">
              <label className="cal-field">
                <span className="cal-label">Fecha</span>
                <input
                  type="date"
                  className="cal-input"
                  value={draftEvent.date}
                  onChange={(event) => setDraftEvent((draft) => ({ ...draft, date: event.target.value }))}
                />
              </label>
              <label className="cal-field">
                <span className="cal-label">Inicio</span>
                <input
                  type="time"
                  className="cal-input"
                  value={draftEvent.startTime}
                  onChange={(event) => setDraftEvent((draft) => ({ ...draft, startTime: event.target.value }))}
                />
              </label>
              <label className="cal-field">
                <span className="cal-label">Fin</span>
                <input
                  type="time"
                  className="cal-input"
                  value={draftEvent.endTime}
                  onChange={(event) => setDraftEvent((draft) => ({ ...draft, endTime: event.target.value }))}
                />
              </label>
            </div>

            <label className="cal-field">
              <span className="cal-label">Notas</span>
              <textarea
                className="cal-textarea"
                value={draftEvent.notes}
                onChange={(event) => setDraftEvent((draft) => ({ ...draft, notes: event.target.value }))}
                placeholder="Detalles, asistentes o recordatorios…"
              />
            </label>

            <div className="cal-dialog-actions">
              {isEditing ? (
                <button
                  type="button"
                  className="cal-btn cal-btn--danger"
                  onClick={() => deleteEvent(draftEvent.id)}
                  style={{ marginRight: "auto" }}
                >
                  <Trash2 size={16} aria-hidden="true" />
                  Eliminar
                </button>
              ) : null}
              <button type="button" className="cal-btn" onClick={() => setIsComposerOpen(false)}>
                Cancelar
              </button>
              <button
                type="button"
                className="cal-btn cal-btn--primary"
                onClick={saveEvent}
                disabled={isSaving || !draftEvent.title.trim()}
              >
                {isSaving ? "Guardando…" : isEditing ? "Guardar cambios" : "Guardar evento"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
